import React, { useEffect, useState } from "react";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import StraightenIcon from "@mui/icons-material/Straighten";
import CustomButton from "../../../../components/buttons/CustomButton";
import { actionButtonSx } from "./editor/EditorToolbar";
import { resolveCalibration, EMPTY_CALIBRATION, MIN_SCALE_DISTANCE_PX } from "../hooks/useCranialEditor";
import { formatConfidence, formatPx } from "../lib/formatMeasurement";
import styles from "./editor/CranialEditor.module.css";

export const MARKER_TYPES = [
  { value: "sticker_20mm", label: "Adesivo de 20 mm", sizeMm: 20, editable: false },
  { value: "coin", label: "Moeda de R$ 1 (27 mm)", sizeMm: 27, editable: false },
  { value: "ruler", label: "Régua (trecho marcado)", sizeMm: 10, editable: true },
  { value: "custom", label: "Outro tamanho", sizeMm: null, editable: true },
];

const STATUS_CHIPS = {
  not_available: { label: "Sem escala", color: "default" },
  not_detected: { label: "Sem escala", color: "default" },
  detected: { label: "Detectada pela IA", color: "primary" },
  manual: { label: "Manual", color: "success" },
  invalid: { label: "Inválida", color: "error" },
};

const NO_SCALE_TEXT =
  "Referência física não identificada. As medidas lineares serão apresentadas em pixels; os índices percentuais continuam disponíveis.";
const PLANE_TEXT =
  "O marcador deve estar no mesmo plano do contorno medido. Uma régua na maca, abaixo da cabeça, distorce a escala.";

const markerFor = (value) => MARKER_TYPES.find((m) => m.value === value) || null;

const parseSize = (text) => {
  const n = Number(String(text).replace(",", ".").trim());
  return Number.isFinite(n) && n > 0 ? n : null;
};

const sizeToText = (value) =>
  Number.isFinite(Number(value)) && Number(value) > 0 ? String(value).replace(".", ",") : "";

function invalidReason(calibration) {
  const points = calibration.points || [];
  if (points.length < 2) return "Marque o segundo ponto do marcador.";
  if (!(Number(calibration.referenceSizeMm) > 0)) return "Informe o tamanho real do marcador.";
  if (!(calibration.referenceSizePx >= MIN_SCALE_DISTANCE_PX)) {
    return "Os pontos estão muito próximos. Aproxime a imagem e marque as extremidades do marcador.";
  }
  return "Revise os pontos e o tamanho do marcador.";
}

/**
 * Scale calibration: marker type, real size and the pixels-per-millimeter result.
 * Emits the full README 3.8 calibration shape through onCalibrationChange(next, meta);
 * `meta.coalesceKey` lets the editor merge consecutive keystrokes into one undo step.
 */
function CalibrationPanel({
  calibration,
  imageWidth,
  imageHeight,
  onCalibrationChange,
  onStartMarking,
  isMarking = false,
}) {
  const current = { ...EMPTY_CALIBRATION, ...(calibration || {}) };
  const points = current.points || [];
  const knownMarker = markerFor(current.markerType);
  const selectValue = knownMarker ? knownMarker.value : current.markerType ? "custom" : "";
  const sizeEditable = !knownMarker || knownMarker.editable;
  const chip = STATUS_CHIPS[current.status] || STATUS_CHIPS.not_detected;

  const [sizeText, setSizeText] = useState(sizeToText(current.referenceSizeMm));
  useEffect(() => {
    if (parseSize(sizeText) !== (Number(current.referenceSizeMm) || null)) {
      setSizeText(sizeToText(current.referenceSizeMm));
    }
    // Only resync when the value changes from outside (undo, marker type, AI suggestion).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.referenceSizeMm]);

  const emit = (patch, meta) => {
    if (!onCalibrationChange) return;
    onCalibrationChange(resolveCalibration(current, patch, imageWidth, imageHeight), meta);
  };

  const handleMarkerChange = (event) => {
    const marker = markerFor(event.target.value);
    if (!marker) return;
    const referenceSizeMm =
      marker.sizeMm !== null ? marker.sizeMm : parseSize(sizeText);
    emit({ markerType: marker.value, referenceSizeMm });
  };

  const handleSizeChange = (event) => {
    const text = event.target.value;
    setSizeText(text);
    emit({ referenceSizeMm: parseSize(text) }, { coalesceKey: "calibration-size" });
  };

  const handleClear = () => {
    if (!onCalibrationChange) return;
    onCalibrationChange(
      { ...EMPTY_CALIBRATION, markerType: current.markerType, referenceSizeMm: current.referenceSizeMm },
      undefined
    );
  };

  const hasScale = Number(current.pixelsPerMillimeter) > 0 && current.status !== "invalid";

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <Typography className={styles.panelTitle}>Escala</Typography>
        <Chip size="small" label={chip.label} color={chip.color} variant={chip.color === "default" ? "outlined" : "filled"} />
      </div>

      <TextField
        select
        fullWidth
        size="small"
        label="Marcador de referência"
        value={selectValue}
        onChange={handleMarkerChange}
      >
        {MARKER_TYPES.map((m) => (
          <MenuItem key={m.value} value={m.value}>
            {m.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        size="small"
        label="Tamanho real"
        value={sizeText}
        onChange={handleSizeChange}
        disabled={!sizeEditable}
        inputProps={{ inputMode: "decimal" }}
        InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
        helperText={
          knownMarker && !knownMarker.editable
            ? "Tamanho padrão do marcador."
            : "Distância real entre os dois pontos marcados."
        }
      />

      {points.length < 2 ? (
        <Typography variant="body2" color="text.secondary">
          {isMarking
            ? "Toque nas duas extremidades do marcador na imagem."
            : "Use o modo Escala para marcar as duas extremidades do marcador na imagem."}
        </Typography>
      ) : null}

      {current.status === "invalid" ? (
        <Typography variant="body2" className={styles.errorText}>
          {invalidReason(current)}
        </Typography>
      ) : null}

      {hasScale ? (
        <div className={styles.scaleResult}>
          <span>
            <strong>
              {Number(current.pixelsPerMillimeter).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} px/mm
            </strong>
          </span>
          <span className={styles.measureSecondary}>
            Referência: {formatPx(current.referenceSizePx)}
            {current.status === "detected" && current.confidence !== null
              ? ` · confiança ${formatConfidence(current.confidence)}`
              : ""}
          </span>
        </div>
      ) : null}

      {!hasScale && current.status !== "invalid" ? (
        <Typography variant="caption" color="text.secondary" component="p">
          {NO_SCALE_TEXT}
        </Typography>
      ) : null}

      {current.status === "detected" ? (
        <Typography variant="caption" color="text.secondary" component="p">
          Confira se os pontos marcam as extremidades do marcador.
        </Typography>
      ) : null}

      <Typography variant="caption" color="text.secondary" component="p">
        {PLANE_TEXT}
      </Typography>

      <div className={styles.panelActions}>
        {onStartMarking && !isMarking ? (
          <CustomButton
            variant="outlined"
            style="primary"
            size="small"
            label="Marcar escala"
            icon={<StraightenIcon fontSize="small" />}
            onClick={onStartMarking}
            sx={actionButtonSx}
          />
        ) : null}
        <CustomButton
          variant="text"
          style="primary"
          size="small"
          label="Limpar escala"
          enabled={points.length > 0}
          onClick={handleClear}
          sx={actionButtonSx}
        />
      </div>
    </section>
  );
}

export default CalibrationPanel;
